import { Fragment, useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { Icon } from '../../components/icons/Icon'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { listComputationRuns } from '../../api/calc'
import { getCreditInstrumentByFiling } from '../../api/credit'
import { getFormInstanceArtifact, getFormTemplateRef, listFinalFormInstancesByFiling } from '../../api/forms'
import { ApiError } from '../../api/problem'
import { useProgramRefs } from '../../lib/useProgramRefs'
import type { ComputationRunView } from '../../types/calc'
import type { CreditInstrumentView } from '../../types/credit'
import type { FormInstanceView, FormTemplateRef } from '../../types/form'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'

export interface CreditPanelProps {
  filings: ProgramFilingDetailView[]
}

interface CreditRow {
  filing: ProgramFilingDetailView
  instrument: CreditInstrumentView
  finalRun: ComputationRunView | null
  finalFormInstances: FormInstanceView[]
}

function downloadPdf(base64: string, filename: string): void {
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * One certified `FormInstance` row (IB-168) — the form's code/name (resolved via `GET
 * /form-templates/{id}`, degrading to `EntityRef`'s generic "Form {shortId}" fallback on a failed
 * lookup, never a fabricated name), its status, and — since a FINAL row always carries a
 * server-rendered PDF (`FormInstanceService.finalizeInstance`) — a real Download action wired to
 * `GET /form-instances/{id}/artifact`. No dead button: Download fetches the actual hashed PDF
 * bytes and saves them, exactly like `DossierPanel`'s own artifact-download action.
 */
function CertifiedFormRow({ instance }: { instance: FormInstanceView }) {
  const [templateRef, setTemplateRef] = useState<FormTemplateRef | null>(null)
  const [downloadState, setDownloadState] = useState<'idle' | 'downloading' | 'error'>('idle')

  useEffect(() => {
    let cancelled = false
    getFormTemplateRef(instance.formTemplateId)
      .then((ref) => {
        if (!cancelled) setTemplateRef(ref)
      })
      .catch(() => {
        // Non-fatal: EntityRef's own generic "Form {shortId}" fallback covers an unresolved name.
      })
    return () => {
      cancelled = true
    }
  }, [instance.formTemplateId])

  async function handleDownload() {
    setDownloadState('downloading')
    try {
      const artifact = await getFormInstanceArtifact(instance.id)
      const filename = `${templateRef?.formCode ?? 'form'}-${instance.id}.pdf`
      downloadPdf(artifact.pdfBase64, filename)
      setDownloadState('idle')
    } catch {
      setDownloadState('error')
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', flexWrap: 'wrap' }}>
      <EntityRef kind="formTemplate" id={instance.formTemplateId} name={templateRef?.formCode} />
      <StatusPill variant="success" label="Final" />
      <button
        type="button"
        className="iw-btn iw-btn--sm"
        onClick={() => void handleDownload()}
        aria-label={`Download ${templateRef?.formCode ?? 'certified form'} PDF`}
      >
        <Icon name="download" size={12} /> {downloadState === 'downloading' ? 'Downloading…' : 'Download'}
      </button>
      {downloadState === 'error' ? (
        <span style={{ color: 'var(--iw-danger)', fontSize: 'var(--iw-size-xs)' }}>Could not download.</span>
      ) : null}
    </div>
  )
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

function formatRunDate(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString()
}

/**
 * The pinned FINAL `ComputationRun`'s human reference — its bound calc version plus the date it
 * was finalized/computed (never its bare `id` as the visible headline; see {@link EntityRef}). The
 * raw id is still available on hover/copy via `EntityRef`'s own affordance.
 */
function finalRunLabel(run: ComputationRunView): string | undefined {
  const version = typeof run.boundCalcVersion === 'number' ? `v${run.boundCalcVersion}` : null
  const when = formatRunDate(run.finalizedAt ?? run.computedAt)
  const parts = [version, when].filter((p): p is string => Boolean(p))
  return parts.length > 0 ? parts.join(' · ') : undefined
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return String(value)
}

const STATUS_VARIANT: Record<string, StatusPillVariant> = {
  certified: 'success',
  transferred: 'info',
}

/**
 * The certified figure's real provenance (case-enrichment story) — the pinned final
 * `ComputationRun`'s `snapshot` (as-of date, bound RuleSet/CalculationGraph versions) and its
 * default Scenario's `computedOutputs`/per-node `provenance` (`{value, inputs[], paramVersion}` —
 * only nodes actually evaluated, `ComputationService`'s own javadoc), rendered verbatim — never a
 * client-side re-derivation of the DAG result. Inline-expand, mirroring `SpendPanel`'s
 * `AdjustmentHistoryRow`/`AuthorityPanel`'s details-row pattern on this same screen.
 */
function ComputationBreakdown({ run }: { run: ComputationRunView }) {
  const scenario = run.scenarios[0]
  const asOf = typeof run.snapshot.asOf === 'string' ? run.snapshot.asOf : null
  const provenanceEntries = scenario ? Object.entries(scenario.provenance) : []
  const outputEntries = scenario ? Object.entries(scenario.computedOutputs) : []
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <div style={{ display: 'flex', gap: 'var(--iw-space-4)', flexWrap: 'wrap', fontSize: 'var(--iw-size-xs)' }}>
        <span>
          <span style={{ color: 'var(--iw-text-muted)' }}>As-of: </span>
          <strong>{asOf ?? '—'}</strong>
        </span>
        <span>
          <span style={{ color: 'var(--iw-text-muted)' }}>Bound RuleSet version: </span>
          <strong>{run.boundRuleSetVersion}</strong>
        </span>
        <span>
          <span style={{ color: 'var(--iw-text-muted)' }}>Bound CalculationGraph version: </span>
          <strong>{run.boundCalcVersion}</strong>
        </span>
        <span>
          <span style={{ color: 'var(--iw-text-muted)' }}>Run kind: </span>
          <strong>{run.kind}</strong>
        </span>
      </div>
      {outputEntries.length > 0 ? (
        <div>
          <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Computed outputs</h4>
          <ul style={{ margin: 0, paddingLeft: 'var(--iw-space-4)' }}>
            {outputEntries.map(([key, value]) => (
              <li key={key} style={{ fontSize: 'var(--iw-size-xs)' }}>
                {key}: <strong>{formatValue(value)}</strong>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {provenanceEntries.length > 0 ? (
        <div>
          <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Calculation breakdown (inputs → node → output)</h4>
          <table className="iw-table" aria-label="Computation node breakdown">
            <thead>
              <tr>
                <th scope="col">Node</th>
                <th scope="col">Value</th>
                <th scope="col">Inputs</th>
                <th scope="col">Parameter version</th>
              </tr>
            </thead>
            <tbody>
              {provenanceEntries.map(([nodeKey, entry]) => (
                <tr key={nodeKey}>
                  <td style={{ fontFamily: 'monospace', fontSize: 'var(--iw-size-xs)' }}>{nodeKey}</td>
                  <td>{formatValue(entry.value)}</td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {entry.inputs.length > 0 ? entry.inputs.join(', ') : '—'}
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {entry.paramVersion ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ margin: 0, color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
          No per-node provenance recorded on this run&apos;s default scenario.
        </p>
      )}
    </div>
  )
}

/**
 * The Case-workspace Credit tab (GA demo v4, enriched by the case-enrichment story) — the certified
 * `CreditInstrument` for each of this Case's filings (`GET .../credit-instrument`, wired onto the
 * already-published `CreditInstrumentApi.getByProgramFilingId` port, see `CertificationController`),
 * plus the pinned `kind==="final"` `ComputationRun` behind it (`GET .../computation-runs`, filtered
 * client-side — this endpoint has no server-side `?kind=` filter), expandable to that run's own
 * as-of date, bound RuleSet/CalculationGraph versions, and full per-node calculation breakdown — the
 * certified number's real provenance, not just its id. A filing that has not certified yet
 * contributes no row here — never a fabricated placeholder.
 *
 * <b>IB-168 — the "no GET resolves a FormInstance by programFilingId" gap is now closed.</b>
 * `GET /program-filings/{id}/form-instances` (`FormInstanceController.listFinal`) resolves this
 * filing's FINAL `FormInstance`(s) directly — no id needed in hand first. Each certified form shows
 * its resolved form code (via `GET /form-templates/{id}`, degrading to a generic fallback on an
 * unresolved lookup), a Final status pill, and a real Download action (`GET
 * .../form-instances/{id}/artifact`) that saves the actual hashed PDF bytes — never a dead button.
 * A filing with no finalized form yet shows "Not yet finalized" — never a fabricated row.
 */
export function CreditPanel({ filings }: CreditPanelProps) {
  const [rows, setRows] = useState<CreditRow[]>([])
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const programRefs = useProgramRefs(filings.map((f) => f.programId))

  useEffect(() => {
    let cancelled = false
    setState('loading')
    setError(null)

    async function load() {
      try {
        const settled = await Promise.all(
          filings.map(async (filing) => {
            const instrument = await getCreditInstrumentByFiling(filing.id)
            if (!instrument) return null
            let finalRun: ComputationRunView | null = null
            try {
              const runs = await listComputationRuns(filing.id)
              finalRun = runs.items.find((r) => r.kind === 'final') ?? null
            } catch {
              // Non-fatal: the certified instrument itself is the headline fact of this row; the
              // pinned run degrades to "—" rather than failing the whole row.
              finalRun = null
            }
            let finalFormInstances: FormInstanceView[] = []
            try {
              finalFormInstances = await listFinalFormInstancesByFiling(filing.id)
            } catch {
              // Non-fatal (IB-168): a denied/unavailable form-instance list degrades this one
              // row's "certified form" cell to "Not yet finalized" rather than failing the row —
              // the certified CreditInstrument amount is still the headline fact either way.
              finalFormInstances = []
            }
            const row: CreditRow = { filing, instrument, finalRun, finalFormInstances }
            return row
          }),
        )
        if (!cancelled) {
          setRows(settled.filter((r): r is CreditRow => r !== null))
          setState('ready')
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load certified credit.')
          setState('error')
        }
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [filings])

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading certified credit…</p>
  }

  if (state === 'error') {
    return <InlineAlert severity="error">{error}</InlineAlert>
  }

  if (rows.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No filing on this case has been certified yet.</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <table className="iw-table" aria-label="Certified credit">
        <thead>
          <tr>
            <th scope="col">Program</th>
            <th scope="col">Certified amount</th>
            <th scope="col">Status</th>
            <th scope="col">Final computation run</th>
            <th scope="col">Breakdown</th>
            <th scope="col">Certified form</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ filing, instrument, finalRun, finalFormInstances }) => {
            const expanded = expandedId === instrument.id
            return (
              <Fragment key={instrument.id}>
                <tr>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    <EntityRef kind="program" id={filing.programId} name={programRefs.get(filing.programId)?.display} />
                  </td>
                  <td>{formatAmount(instrument.certifiedAmount, instrument.currency)}</td>
                  <td>
                    <StatusPill variant={STATUS_VARIANT[instrument.status] ?? 'neutral'} label={instrument.status} />
                  </td>
                  <td style={{ fontSize: 'var(--iw-size-xs)' }}>
                    {finalRun ? (
                      <EntityRef kind="record" id={finalRun.id} name={finalRunLabel(finalRun)} />
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {finalRun ? (
                      <button
                        type="button"
                        className="iw-btn iw-btn--sm"
                        aria-expanded={expanded}
                        aria-label="Calculation breakdown"
                        onClick={() => setExpandedId(expanded ? null : instrument.id)}
                      >
                        {expanded ? 'Hide breakdown' : 'Breakdown'}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    {finalFormInstances.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
                        {finalFormInstances.map((instance) => (
                          <CertifiedFormRow key={instance.id} instance={instance} />
                        ))}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--iw-text-subtle)' }}>Not yet finalized</span>
                    )}
                  </td>
                </tr>
                {expanded && finalRun ? (
                  <tr>
                    <td colSpan={6}>
                      <ComputationBreakdown run={finalRun} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
